import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { StatsCard } from '@/components/StatsCard';
import { Chart } from '@/components/Chart';
import { DataTable } from '@/components/DataTable';
import { DatePicker } from '@/components/DatePicker';
import { Button } from '@/components/Button';
import {
  listPushApps,
  getPushAnalytics,
  type PushAnalyticsCampaign,
  type PushAnalyticsApp,
  type PushAnalyticsLocale,
} from '@/api/push';

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const pct = (r: number) => `${(r * 100).toFixed(1)}%`;
const num = (n: number) => n.toLocaleString();

export function PushAnalytics() {
  const [from, setFrom] = useState(ymd(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [to, setTo] = useState(ymd(new Date()));
  const [appId, setAppId] = useState('all');

  const { data: appsData } = useQuery({ queryKey: ['push-apps'], queryFn: listPushApps });
  const apps = appsData?.apps ?? [];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['push-analytics', from, to, appId],
    queryFn: () => getPushAnalytics({ from, to, app_id: appId }),
  });

  const summary = data?.summary;
  const byDay = data?.byDay ?? [];
  const byDayErrors = useMemo(
    () => byDay.map((d) => ({ date: d.date, errors: d.failed + d.token_invalid })),
    [byDay],
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Push · Analytics</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <DatePicker label="From" value={from} onChange={setFrom} />
        <DatePicker label="To" value={to} onChange={setTo} />
        <div>
          <label htmlFor="app-filter" className="block text-sm font-medium text-content-muted mb-1">App</label>
          <select
            id="app-filter"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface border border-border text-content text-sm"
          >
            <option value="all">All apps</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-danger mb-3">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : isLoading || !summary ? (
        <div className="text-sm text-content-subtle">Loading…</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <StatsCard label="Sent" value={num(summary.sent)} />
            <StatsCard label="Failed" value={num(summary.failed)} />
            <StatsCard label="Token invalid" value={num(summary.token_invalid)} />
            <StatsCard label="Delivery rate" value={pct(summary.deliveryRate)} />
            <StatsCard label="Campaigns" value={num(summary.campaigns)} />
          </div>

          {/* Per-day trend (two single-line charts) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4">
              <div className="text-xs uppercase text-content-subtle mb-2">Sent / day</div>
              <Chart data={byDay} dataKey="sent" xAxisKey="date" height={240} />
            </div>
            <div className="bg-surface border border-border rounded-lg p-4">
              <div className="text-xs uppercase text-content-subtle mb-2">Errors / day</div>
              <Chart data={byDayErrors} dataKey="errors" xAxisKey="date" height={240} />
            </div>
          </div>

          {/* Per-campaign table */}
          <h2 className="text-sm font-semibold text-content mb-2">By campaign</h2>
          <div className="mb-6">
            <DataTable<PushAnalyticsCampaign>
              data={data.byCampaign}
              keyExtractor={(r) => r.schedule_id}
              columns={[
                {
                  key: 'name', label: 'Campaign',
                  render: (r) => (
                    <Link to={`/push/schedules/${r.schedule_id}`} className="text-accent hover:underline">{r.name}</Link>
                  ),
                },
                { key: 'app_name', label: 'App', render: (r) => <span className="text-content-muted">{r.app_name}</span> },
                { key: 'sent', label: 'Sent', render: (r) => num(r.sent) },
                { key: 'failed', label: 'Failed', render: (r) => num(r.failed) },
                { key: 'token_invalid', label: 'Token invalid', render: (r) => num(r.token_invalid) },
                { key: 'deliveryRate', label: 'Delivery rate', render: (r) => pct(r.deliveryRate) },
              ]}
            />
          </div>

          {/* Per-app + per-locale breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-semibold text-content mb-2">By app</h2>
              <DataTable<PushAnalyticsApp>
                data={data.byApp}
                keyExtractor={(r) => r.app_id}
                columns={[
                  { key: 'app_name', label: 'App' },
                  { key: 'sent', label: 'Sent', render: (r) => num(r.sent) },
                  { key: 'failed', label: 'Failed', render: (r) => num(r.failed) },
                  { key: 'deliveryRate', label: 'Delivery rate', render: (r) => pct(r.deliveryRate) },
                ]}
              />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-content mb-2">By locale</h2>
              <DataTable<PushAnalyticsLocale>
                data={data.byLocale}
                keyExtractor={(r) => r.locale}
                columns={[
                  { key: 'locale', label: 'Locale' },
                  { key: 'sent', label: 'Sent', render: (r) => num(r.sent) },
                  { key: 'failed', label: 'Failed', render: (r) => num(r.failed) },
                  { key: 'deliveryRate', label: 'Delivery rate', render: (r) => pct(r.deliveryRate) },
                ]}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
